import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { NodeData, NodeType, CanvasNode, StoryboardBox } from '../../types';
import { AI_MODELS, getImageModels, calculateCost } from '../../models/config';
import { 
  LayoutGrid, Loader2, Sparkles, AlertCircle, Plus, Minus, 
  Play, Square, Link as LinkIcon, X, RectangleHorizontal 
} from 'lucide-react';
import { useFileUrls } from '../../hooks/useFileUrl';
import { FileImage } from '../FileImage';
import { loadMediaAsBase64 } from '../../utils/mediaLoader';
import { autoSaveFile, generateSaveFilename, isAutoSaveReady, registerLocalFileMapping, CustomWorkflow } from '../../utils/localSave';
import { getComfyUIClient, buildTxt2ImgWorkflow, buildFluxWorkflow, buildFluxUnetWorkflow, aspectRatioToSize, convertWorkflowToApi } from '../../utils/comfyui';
import { uploadWithDeduplication } from '../../utils/fileRegistry';
import { nodeTone, type NodeTone } from './nodeTheme';
import { SettingChip } from './composer/Composer';
import { PROVIDER_NAMES, shortModelLabel } from './composer/modelLabels';

interface StoryboardNodeProps {
  data: NodeData;
  onDataChange: (newData: NodeData) => void;
  inputImages?: { id: string; url: string; label?: string }[];
  inputEntities?: CanvasNode[];
  onRemoveConnection?: (connectionId: string) => void;
  googleApiKey?: string;
  onRequestApiKey?: () => void;
  userId?: string;
  workspaceName?: string;
  nodeId?: string;
  onAutoSaveStatus?: (type: 'local' | 'cloud' | 'both', message: string) => void;
  onAutoSaveFile?: (fileData: string | Blob, fileName: string, category?: 'Generated_Images' | 'Videos' | 'Audio' | 'Sketches' | 'Uploads', fileExtension?: string) => Promise<void>;
  customWorkflows?: CustomWorkflow[];
  isViewOnly?: boolean;
  ownerUserId?: string;
}

const generateBoxId = () => Math.random().toString(36).substr(2, 9);

const DEFAULT_COLUMNS = 4;
const DEFAULT_ROWS = 3;
// The height of every frame's prompt field: three lines. (No textarea in a card has a grip to
// drag, so this is the height they keep.)
const DEFAULT_TEXTAREA_HEIGHT = 74;

export const StoryboardNode: React.FC<StoryboardNodeProps> = ({
  data,
  onDataChange,
  inputImages = [],
  inputEntities = [],
  onRemoveConnection,
  googleApiKey,
  onRequestApiKey,
  userId,
  workspaceName = 'Untitled',
  nodeId,
  onAutoSaveStatus,
  onAutoSaveFile,
  customWorkflows = [],
  isViewOnly = false,
  ownerUserId,
}) => {
  const [isGeneratingAll, setIsGeneratingAll] = useState(false);
  const [showApiKeyPrompt, setShowApiKeyPrompt] = useState(false);
  const [textareaHeight, setTextareaHeight] = useState(DEFAULT_TEXTAREA_HEIGHT);
  const isCancelledRef = useRef(false);
  const currentGeneratingBoxRef = useRef<string | null>(null);
  
  // Drag and drop state (using pointer events for better compatibility)
  const [draggedBoxId, setDraggedBoxId] = useState<string | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [isActiveDrag, setIsActiveDrag] = useState(false);
  const [ghostPosition, setGhostPosition] = useState<{ x: number; y: number } | null>(null);
  const [insertionIndex, setInsertionIndex] = useState<number | null>(null);
  const [draggedImageSrc, setDraggedImageSrc] = useState<string | null>(null);
  const dragStartPos = useRef<{ x: number; y: number } | null>(null);
  const boxRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const gridRef = useRef<HTMLDivElement>(null);

  // Initialize storyboard config
  const config = data.storyboardConfig || {
    model: 'gemini-2.5-flash-image',
    aspectRatio: '16:9',
    columns: DEFAULT_COLUMNS,
    rows: DEFAULT_ROWS,
    boxes: [],
  };

  // Ensure boxes array is populated
  const boxes: StoryboardBox[] = useMemo(() => {
    const totalBoxes = config.columns * config.rows;
    const existingBoxes = config.boxes || [];
    
    if (existingBoxes.length === totalBoxes) {
      return existingBoxes;
    }
    
    // Create or trim boxes to match grid size
    const newBoxes: StoryboardBox[] = [];
    for (let i = 0; i < totalBoxes; i++) {
      if (existingBoxes[i]) {
        newBoxes.push(existingBoxes[i]);
      } else {
        newBoxes.push({
          id: generateBoxId(),
          prompt: '',
          status: 'idle',
        });
      }
    }
    return newBoxes;
  }, [config.columns, config.rows, config.boxes]);

  const currentModelId = config.model || 'gemini-2.5-flash-image';
  const modelDef = AI_MODELS[currentModelId] || AI_MODELS['gemini-2.5-flash-image'];
  const aspectRatio = config.aspectRatio || '16:9';

  // Storyboard frames render through the Google path only; Venice image models live in the Gen Image node.
  const builtInModels = useMemo(() => getImageModels().filter(m => m.provider !== 'venice'), []);
  // The model chip's list, under the providers' names as in the Image node
  const modelOptions = useMemo(
    () => builtInModels.map(m => ({ value: m.id, label: shortModelLabel(m.label), group: PROVIDER_NAMES[m.provider] ?? m.provider })),
    [builtInModels],
  );

  const updateConfig = useCallback((updates: Partial<typeof config>) => {
    if (isViewOnly) return;
    onDataChange({
      ...data,
      storyboardConfig: {
        ...config,
        ...updates,
      },
    });
  }, [data, config, onDataChange, isViewOnly]);

  const updateBox = useCallback((boxId: string, updates: Partial<StoryboardBox>) => {
    const newBoxes = boxes.map(box =>
      box.id === boxId ? { ...box, ...updates } : box
    );
    updateConfig({ boxes: newBoxes });
  }, [boxes, updateConfig]);

  // Delete a box from the storyboard
  const deleteBox = useCallback((boxId: string) => {
    const newBoxes = boxes.filter(box => box.id !== boxId);
    updateConfig({ boxes: newBoxes });
  }, [boxes, updateConfig]);

  // Register box ref for hit testing
  const registerBoxRef = useCallback((boxId: string, el: HTMLDivElement | null) => {
    if (el) {
      boxRefs.current.set(boxId, el);
    } else {
      boxRefs.current.delete(boxId);
    }
  }, []);

  // Find which box is under the pointer
  const findBoxUnderPointer = useCallback((clientX: number, clientY: number): number | null => {
    for (let i = 0; i < boxes.length; i++) {
      const box = boxes[i];
      const el = boxRefs.current.get(box.id);
      if (el && box.id !== draggedBoxId) {
        const rect = el.getBoundingClientRect();
        if (clientX >= rect.left && clientX <= rect.right && 
            clientY >= rect.top && clientY <= rect.bottom) {
          return i;
        }
      }
    }
    return null;
  }, [boxes, draggedBoxId]);

  // Pointer-based drag handlers (using window events for reliable tracking)
  const handleBoxPointerDown = useCallback((boxId: string, e: React.PointerEvent) => {
    // Only start drag on left mouse button or touch
    if (e.button !== 0) return;
    
    // Don't start drag if clicking on textarea or button
    const target = e.target as HTMLElement;
    if (target.tagName === 'TEXTAREA' || target.tagName === 'BUTTON' || target.closest('button')) {
      return;
    }
    
    e.preventDefault();
    e.stopPropagation();
    
    // Capture the already-loaded image from the card (instant, no re-fetch needed)
    const cardEl = boxRefs.current.get(boxId);
    const imgEl = cardEl?.querySelector('img');
    if (imgEl && imgEl.complete && imgEl.src) {
      setDraggedImageSrc(imgEl.src);
    } else {
      setDraggedImageSrc(null);
    }
    
    dragStartPos.current = { x: e.clientX, y: e.clientY };
    setDraggedBoxId(boxId);
    setIsActiveDrag(false);
  }, []);

  // Calculate insertion index based on cursor position relative to cards
  const findInsertionIndex = useCallback((clientX: number, clientY: number): number | null => {
    const sourceIndex = boxes.findIndex(box => box.id === draggedBoxId);
    if (sourceIndex === -1) return null;

    // Find the closest insertion point
    let closestIndex: number | null = null;
    let closestDistance = Infinity;

    for (let i = 0; i <= boxes.length; i++) {
      // Skip positions that would result in no change
      if (i === sourceIndex || i === sourceIndex + 1) continue;

      let targetX: number;
      let targetY: number;

      if (i < boxes.length) {
        const box = boxes[i];
        const el = boxRefs.current.get(box.id);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        targetX = rect.left; // Left edge of the card
        targetY = rect.top + rect.height / 2;
      } else {
        // After the last card
        const lastBox = boxes[boxes.length - 1];
        const el = boxRefs.current.get(lastBox.id);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        targetX = rect.right; // Right edge of last card
        targetY = rect.top + rect.height / 2;
      }

      const distance = Math.sqrt(
        Math.pow(clientX - targetX, 2) + Math.pow(clientY - targetY, 2)
      );

      if (distance < closestDistance && distance < 150) {
        closestDistance = distance;
        closestIndex = i;
      }
    }

    return closestIndex;
  }, [boxes, draggedBoxId]);

  // Use window events for reliable drag tracking
  useEffect(() => {
    if (!draggedBoxId) return;

    const handlePointerMove = (e: PointerEvent) => {
      if (!dragStartPos.current) return;
      
      // Check if we've moved enough to consider it a drag
      const dx = Math.abs(e.clientX - dragStartPos.current.x);
      const dy = Math.abs(e.clientY - dragStartPos.current.y);
      
      if (dx > 5 || dy > 5) {
        setIsActiveDrag(true);
        
        // Update ghost position to follow cursor
        setGhostPosition({ x: e.clientX, y: e.clientY });
        
        // Find insertion point
        const insertIdx = findInsertionIndex(e.clientX, e.clientY);
        setInsertionIndex(insertIdx);
        
        // Also track which box we're over (for legacy compatibility)
        const overIndex = findBoxUnderPointer(e.clientX, e.clientY);
        setDragOverIndex(overIndex);
      }
    };

    const handlePointerUp = () => {
      if (isActiveDrag && insertionIndex !== null) {
        const sourceIndex = boxes.findIndex(box => box.id === draggedBoxId);
        if (sourceIndex !== -1) {
          // Calculate actual target index accounting for removal
          let targetIndex = insertionIndex;
          if (sourceIndex < insertionIndex) {
            targetIndex = insertionIndex - 1;
          }
          
          if (sourceIndex !== targetIndex) {
            // Reorder boxes
            const newBoxes = [...boxes];
            const [movedBox] = newBoxes.splice(sourceIndex, 1);
            newBoxes.splice(targetIndex, 0, movedBox);
            updateConfig({ boxes: newBoxes });
          }
        }
      }
      
      setDraggedBoxId(null);
      setDragOverIndex(null);
      setIsActiveDrag(false);
      setGhostPosition(null);
      setInsertionIndex(null);
      setDraggedImageSrc(null);
      dragStartPos.current = null;
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [draggedBoxId, isActiveDrag, insertionIndex, boxes, findBoxUnderPointer, findInsertionIndex, updateConfig]);

  // Handle textarea resize - apply to all boxes
  const handleTextareaResize = useCallback((newHeight: number) => {
    if (newHeight !== textareaHeight && newHeight >= 40) {
      setTextareaHeight(newHeight);
    }
  }, [textareaHeight]);

  // Check API key availability
  const checkApiKey = async (): Promise<boolean> => {
    if (modelDef.provider === 'comfyui') return true;
    if (modelDef.provider === 'google' && googleApiKey) return true;
    setShowApiKeyPrompt(true);
    return false;
  };

  const handleSelectKey = () => {
    setShowApiKeyPrompt(false);
    onRequestApiKey?.();
  };

  // Get augmented prompt with entity context
  const getAugmentedPrompt = (basePrompt: string): string => {
    if (inputEntities.length === 0) return basePrompt;
    
    let finalPrompt = basePrompt;
    const mentionedEntities: string[] = [];
    const unmentionedEntities: { name: string; desc: string }[] = [];
    
    // Separate entities into mentioned and unmentioned
    inputEntities.forEach(entity => {
      const name = entity.data.entityData?.name;
      const desc = entity.data.entityData?.description;
      if (!name || !desc) return;
      
      if (finalPrompt.toLowerCase().includes(name.toLowerCase())) {
        // Entity is mentioned - add inline description
        mentionedEntities.push(name);
        finalPrompt += ` (${name} visual appearance: ${desc})`;
      } else {
        // Entity not mentioned - collect for context prefix
        unmentionedEntities.push({ name, desc });
      }
    });
    
    // Add unmentioned entities as context prefix
    if (unmentionedEntities.length > 0) {
      const contextPrefix = unmentionedEntities
        .map(e => `[${e.name}: ${e.desc}]`)
        .join(' ');
      finalPrompt = `Context - ${contextPrefix}. Scene: ${finalPrompt}`;
    }
    
    return finalPrompt;
  };

  /** Bytes of a connected image for the provider request; errors name the input. */
  const getBase64FromUrl = (url: string, label = 'the reference image') =>
    loadMediaAsBase64(url, { userId, ownerUserId, workspaceName, label, defaultMimeType: 'image/png' });

  // Generate single image for a box
  const generateSingleBox = async (boxId: string): Promise<void> => {
    if (isViewOnly) return;
    const box = boxes.find(b => b.id === boxId);
    if (!box || !box.prompt.trim()) return;

    const hasKey = await checkApiKey();
    if (!hasKey) return;

    currentGeneratingBoxRef.current = boxId;
    updateBox(boxId, { status: 'generating', errorMessage: undefined });

    try {
      const promptText = getAugmentedPrompt(box.prompt);

      if (modelDef.provider === 'google') {
        // Gemini API
        const url = `https://generativelanguage.googleapis.com/v1beta/models/${currentModelId}:generateContent?key=${googleApiKey}`;
        
        const parts: any[] = [{ text: promptText }];
        
        // Add reference images
        if (inputImages.length > 0 && modelDef.supportsImageReference) {
          for (const img of inputImages.slice(0, modelDef.maxReferenceImages || 1)) {
            try {
              const { data, mimeType } = await getBase64FromUrl(img.url, img.label || 'the reference image');
              parts.push({ inlineData: { mimeType, data } });
            } catch (e) {
              console.error('Failed to load reference image', img.url, e);
            }
          }
          parts[0].text += '\n\nUse the provided reference image for visual style and subject matter.';
        }

        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contents: [{ parts }],
            generationConfig: {
              responseModalities: currentModelId.includes('flash') ? ['TEXT', 'IMAGE'] : ['IMAGE'],
              imageConfig: { aspectRatio },
            },
          }),
        });

        const payload = await response.json();
        if (!response.ok) {
          throw new Error(payload?.error?.message || response.statusText);
        }

        let imageUrl: string | null = null;
        if (payload.candidates?.length > 0) {
          for (const candidate of payload.candidates) {
            const part = candidate.content?.parts?.find((p: any) => p.inlineData);
            if (part?.inlineData) {
              imageUrl = `data:${part.inlineData.mimeType};base64,${part.inlineData.data}`;
              break;
            }
          }
        }

        if (!imageUrl) {
          throw new Error('No image generated');
        }

        // Auto-save (both local and Google Drive if enabled)
        const timestamp = Date.now();
        // The cloud copy's name (category and time): a lookup by that name finds the local copy
        const filename = generateSaveFilename('Generated_Images', 'png', timestamp);
        if (onAutoSaveFile && imageUrl.startsWith('data:')) {
          try {
            await onAutoSaveFile(imageUrl, filename, 'Generated_Images', 'png');
          } catch (autoSaveError) {
            console.warn('Storyboard auto-save failed:', autoSaveError);
          }
        }

        // Upload to cloud
        let finalImageUrl = imageUrl;
        if (userId && imageUrl.startsWith('data:')) {
          try {
            const fileRef = await uploadWithDeduplication(userId, imageUrl, {
              category: 'Generated_Images',
              workspaceName,
              displayNamePrefix: 'storyboard',
              extension: 'png',
              nodeId,
              timestamp,
            });
            if (fileRef.startsWith('file://')) registerLocalFileMapping(fileRef, `Generated_Images/${filename}`);
            finalImageUrl = fileRef;
          } catch (e) {
            console.warn('Upload failed, keeping base64:', e);
          }
        }

        updateBox(boxId, { imageUrl: finalImageUrl, status: 'completed' });

      } else if (modelDef.provider === 'comfyui') {
        // ComfyUI generation
        const client = getComfyUIClient();
        const checkpoints = await client.getCheckpoints();
        const selectedCheckpoint = localStorage.getItem('comfyui_default_checkpoint') || checkpoints[0];
        
        if (!selectedCheckpoint && !currentModelId.includes('flux')) {
          throw new Error('No checkpoint model found. Please configure a default checkpoint in Settings → Local Models.');
        }

        let workflow: Record<string, any>;
        const isFlux = currentModelId.includes('flux');

        if (isFlux) {
          const unets = await client.getUnets();
          const unetMatch = unets.find(u => 
            u.toLowerCase().includes('schnell') || u.toLowerCase().includes('flux')
          );
          
          if (unetMatch) {
            const clips = await client.getClips();
            const vaes = await client.getVaes();
            const clipT5 = clips.find(c => c.toLowerCase().includes('t5'));
            const clipL = clips.find(c => c.toLowerCase().includes('clip_l'));
            const fluxVae = vaes.find(v => v.toLowerCase().includes('ae') || v.toLowerCase().includes('flux'));
            
            const size = aspectRatioToSize(aspectRatio);
            workflow = buildFluxUnetWorkflow({
              unet: unetMatch,
              clip1: clipT5 || clips[0] || '',
              clip2: clipL || clips[1] || '',
              vae: fluxVae || vaes[0] || '',
              prompt: promptText,
              width: size.width,
              height: size.height,
              steps: 4,
            });
          } else {
            const fluxCheckpoint = checkpoints.find(c => c.toLowerCase().includes('flux'));
            if (!fluxCheckpoint) throw new Error('No FLUX model found');
            const size = aspectRatioToSize(aspectRatio);
            workflow = buildFluxWorkflow({
              checkpoint: fluxCheckpoint,
              prompt: promptText,
              width: size.width,
              height: size.height,
              steps: 4,
            });
          }
        } else {
          const size = aspectRatioToSize(aspectRatio);
          workflow = buildTxt2ImgWorkflow({
            checkpoint: selectedCheckpoint,
            prompt: promptText,
            negativePrompt: 'blurry, low quality, distorted',
            width: size.width,
            height: size.height,
            steps: 20,
            cfg: 7,
          });
        }

        // Queue the prompt
        const queued = await client.queuePrompt(workflow);
        console.log('ComfyUI queued prompt:', queued.prompt_id);
        
        // Wait for completion
        const result = await client.waitForCompletion(queued.prompt_id);
        
        if (isCancelledRef.current) {
          updateBox(boxId, { status: 'idle' });
          return;
        }

        // Check if we got valid outputs
        if (!result || !result.outputs) {
          throw new Error('No output received from ComfyUI');
        }

        // Find the last output with images (final stage of workflow)
        const outputs = Object.values(result.outputs);
        const imageOutputs = outputs.filter((o: any) => o.images && o.images.length > 0);
        const imageOutput = imageOutputs[imageOutputs.length - 1] as any;
        
        if (imageOutput?.images?.[0]) {
          const imageInfo = imageOutput.images[0];
          
          // Download the generated image
          const imageBlob = await client.getImage(imageInfo.filename, imageInfo.subfolder, imageInfo.type);
          
          // Convert blob to data URL
          const reader = new FileReader();
          const dataUrl = await new Promise<string>((resolve) => {
            reader.onloadend = () => resolve(reader.result as string);
            reader.readAsDataURL(imageBlob);
          });
          
          // Auto-save (both local and Google Drive if enabled)
          const timestamp = Date.now();
          // The cloud copy's name (category and time): a lookup by that name finds the local copy
          const filename = generateSaveFilename('Generated_Images', 'png', timestamp);
          if (onAutoSaveFile) {
            try {
              await onAutoSaveFile(dataUrl, filename, 'Generated_Images', 'png');
            } catch (autoSaveError) {
              console.warn('ComfyUI Storyboard auto-save failed:', autoSaveError);
            }
          }

          // Upload to cloud if user is logged in
          let finalImageUrl = dataUrl;
          if (userId) {
            try {
              const fileRef = await uploadWithDeduplication(userId, dataUrl, {
                category: 'Generated_Images',
                workspaceName,
                displayNamePrefix: 'storyboard',
                extension: 'png',
                nodeId,
                timestamp,
              });
              if (fileRef.startsWith('file://')) registerLocalFileMapping(fileRef, `Generated_Images/${filename}`);
              finalImageUrl = fileRef;
            } catch (e) {
              console.warn('Upload failed, keeping data URL:', e);
            }
          }
          
          updateBox(boxId, { imageUrl: finalImageUrl, status: 'completed' });
        } else {
          throw new Error('No image output from ComfyUI. Check that the workflow has a SaveImage node.');
        }
      }
    } catch (error: any) {
      if (!isCancelledRef.current) {
        console.error('Generation error:', error);
        updateBox(boxId, { status: 'error', errorMessage: error.message || 'Generation failed' });
      }
    } finally {
      currentGeneratingBoxRef.current = null;
    }
  };

  // Generate all boxes with prompts sequentially
  const generateAll = async () => {
    if (isViewOnly) return;
    const hasKey = await checkApiKey();
    if (!hasKey) return;

    isCancelledRef.current = false;
    setIsGeneratingAll(true);

    const boxesWithPrompts = boxes.filter(b => b.prompt.trim());
    
    for (const box of boxesWithPrompts) {
      if (isCancelledRef.current) break;
      await generateSingleBox(box.id);
    }

    setIsGeneratingAll(false);
  };

  // Stop all generation
  const stopGeneration = async () => {
    isCancelledRef.current = true;
    
    if (modelDef.provider === 'comfyui') {
      try {
        const client = getComfyUIClient();
        await client.interrupt();
      } catch (e) {
        console.error('Failed to interrupt ComfyUI:', e);
      }
    }

    // Reset any generating boxes
    const newBoxes = boxes.map(box =>
      box.status === 'generating' ? { ...box, status: 'idle' as const } : box
    );
    updateConfig({ boxes: newBoxes });
    setIsGeneratingAll(false);
  };

  // Add a row
  const addRow = () => {
    if (isViewOnly) return;
    const newRows = config.rows + 1;
    const newBoxes = [...boxes];
    for (let i = 0; i < config.columns; i++) {
      newBoxes.push({
        id: generateBoxId(),
        prompt: '',
        status: 'idle',
      });
    }
    updateConfig({ rows: newRows, boxes: newBoxes });
  };

  // Remove a row (if more than 1)
  const removeRow = () => {
    if (isViewOnly) return;
    if (config.rows <= 1) return;
    const newRows = config.rows - 1;
    const newBoxes = boxes.slice(0, newRows * config.columns);
    updateConfig({ rows: newRows, boxes: newBoxes });
  };

  const boxesWithPrompts = boxes.filter(b => b.prompt.trim()).length;
  const isAnyGenerating = boxes.some(b => b.status === 'generating') || isGeneratingAll;

  // The card's header says "Storyboard"; the strip under it holds the board's size and its two
  // settings, each a chip that opens a list, as in the Image and Video nodes.
  return (
    <div
      className="w-full h-full flex flex-col bg-white overflow-hidden node-scroll-trap"
      onWheel={(e) => e.stopPropagation()}
      style={{ touchAction: 'pan-y' }}
    >
      {/* The board's size, the model and the frames' shape */}
      <div className="shrink-0 bg-white px-3 py-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 border-b border-cream-200">
        <span className="ns-t-meta whitespace-nowrap">
          {config.columns}×{config.rows} ({boxes.length} {boxes.length === 1 ? 'frame' : 'frames'})
        </span>

        <div className="flex flex-wrap items-center gap-1.5 min-w-0">
          <SettingChip
            icon={<Sparkles size={14} />}
            label="Model"
            value={currentModelId}
            options={modelOptions}
            onChange={(model) => updateConfig({ model })}
            disabled={isAnyGenerating || isViewOnly}
            shrink
          />

          {modelDef.supportedRatios && (
            <SettingChip
              icon={<RectangleHorizontal size={14} />}
              label="Aspect ratio"
              value={aspectRatio}
              display={aspectRatio}
              options={modelDef.supportedRatios}
              onChange={(ratio) => updateConfig({ aspectRatio: ratio })}
              disabled={isAnyGenerating || isViewOnly}
            />
          )}
        </div>
      </div>

      {/* CONTEXT (Connected entities/images) */}
      {(inputImages.length > 0 || inputEntities.length > 0) && (
        <div className="bg-cream-50 px-3 py-2.5 border-b border-cream-200">
          <span className="ns-t-label flex items-center gap-1 mb-2">
            <LinkIcon size={12} /> Context
          </span>

          {/* Reference Images - Thumbnails with labels below */}
          {inputImages.length > 0 && (
            <div className="flex gap-3 flex-wrap mb-2">
              {inputImages.slice(0, 6).map((img, idx) => (
                <div key={`${img.id}-${idx}`} className="flex flex-col items-center" title={img.label || 'Image'}>
                  <div className="relative group w-[60px] h-[60px] rounded-lg overflow-hidden border border-cream-300 bg-white shrink-0">
                    <FileImage src={img.url} userId={userId} workspaceName={workspaceName} className="w-full h-full object-cover" alt="" />
                    <button
                      onClick={() => onRemoveConnection?.(img.id)}
                      className="node-reveal ns-icon-btn ns-icon-btn-xs ns-icon-btn-dark absolute inset-0 m-auto opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
                      title="Remove from context"
                      aria-label="Remove from context"
                    >
                      <X />
                    </button>
                  </div>
                  <span className="text-2xs text-ink-soft truncate max-w-[60px] text-center mt-1">
                    {img.label || 'Image'}
                  </span>
                </div>
              ))}
              {inputImages.length > 6 && (
                <span className="ns-t-meta self-center">+{inputImages.length - 6}</span>
              )}
            </div>
          )}

          {/* Entity/Character References: each chip in the colour of its own kind of node */}
          {inputEntities.length > 0 && (
            <div className="flex gap-1.5 flex-wrap">
              {inputEntities.map(e => {
                const entityName = e.data.entityData?.name || 'Entity';
                const entityDesc = e.data.entityData?.description || '';

                return (
                  <span
                    key={e.id}
                    data-tone={nodeTone(e.type)}
                    className="ns-chip ns-chip-sm ns-chip-tone"
                    title={`${entityName}: ${entityDesc || 'No description'}\n\nDescription auto-injected into prompts.`}
                  >
                    <span className="ns-tone-dot" />
                    {entityName}
                  </span>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* GRID */}
      <div className="flex-1 min-h-0 overflow-y-auto p-3 bg-cream-50 relative node-scrollable-content">
        <div
          ref={gridRef}
          className="grid gap-3"
          style={{
            gridTemplateColumns: `repeat(${config.columns}, 1fr)`,
          }}
        >
          {boxes.map((box, index) => {
            const isDraggedCard = draggedBoxId === box.id && isActiveDrag;
            const draggedIndex = draggedBoxId ? boxes.findIndex(b => b.id === draggedBoxId) : -1;

            // Calculate if this card should show a drop indicator
            // Show indicator on the LEFT of this card if insertion is at this index
            const showLeftIndicator = isActiveDrag && insertionIndex === index &&
              insertionIndex !== draggedIndex && insertionIndex !== draggedIndex + 1;

            // Show indicator on the RIGHT of this card if it's the last card and insertion is after it
            const showRightIndicator = isActiveDrag && index === boxes.length - 1 &&
              insertionIndex === boxes.length && draggedIndex !== boxes.length - 1;

            return (
              <StoryboardBoxComponent
                key={box.id}
                box={box}
                index={index}
                aspectRatio={aspectRatio}
                textareaHeight={textareaHeight}
                onTextareaResize={handleTextareaResize}
                isGenerating={box.status === 'generating'}
                disabled={isAnyGenerating && box.status !== 'generating'}
                onPromptChange={(prompt) => updateBox(box.id, { prompt })}
                onGenerate={() => generateSingleBox(box.id)}
                onDismissError={() => updateBox(box.id, { status: 'idle', errorMessage: undefined })}
                onDelete={() => deleteBox(box.id)}
                userId={userId}
                workspaceName={workspaceName}
                isDragging={isDraggedCard}
                isDragOver={false}
                showDropIndicator={showLeftIndicator}
                showDropIndicatorAfter={showRightIndicator}
                onPointerDown={(e) => handleBoxPointerDown(box.id, e)}
                registerRef={(el) => registerBoxRef(box.id, el)}
                availableEntities={inputEntities.map(e => ({
                  id: e.id,
                  name: e.data.entityData?.name || 'Entity',
                  type: e.type === 'CHARACTER_NODE' ? 'Character' : e.type === 'PROP_NODE' ? 'Prop' : e.type === 'LOCATION_NODE' ? 'Location' : 'Character',
                  tone: nodeTone(e.type),
                }))}
              />
            );
          })}
        </div>

        {/* Floating Ghost Card - rendered via portal to escape canvas transforms */}
        {isActiveDrag && ghostPosition && draggedBoxId && (() => {
          const draggedBox = boxes.find(b => b.id === draggedBoxId);
          if (!draggedBox) return null;
          const draggedIndex = boxes.findIndex(b => b.id === draggedBoxId);

          // The ghost is outside the node's card, so it is given the storyboard's tone itself
          return createPortal(
            <div
              className="fixed pointer-events-none z-[130]"
              data-tone={nodeTone(NodeType.STORYBOARD_NODE)}
              style={{
                left: ghostPosition.x,
                top: ghostPosition.y,
                transform: 'translate(-50%, -50%) rotate(2deg) scale(1.05)',
              }}
            >
              <div className="w-40 bg-white rounded-lg overflow-hidden border-2 border-[color:var(--tone)] shadow-float opacity-95">
                {/* Mini card preview */}
                <div className="h-7 px-2 flex items-center border-b border-cream-200">
                  <span className="ns-badge ns-badge-tone">#{draggedIndex + 1}</span>
                </div>
                <div className="relative w-full bg-cream-200" style={{ paddingBottom: '56.25%' }}>
                  <div className="absolute inset-0 flex items-center justify-center">
                    {draggedImageSrc ? (
                      <img
                        src={draggedImageSrc}
                        className="w-full h-full object-cover"
                        alt=""
                      />
                    ) : (
                      <div className="text-ink-faint">
                        <LayoutGrid size={20} />
                      </div>
                    )}
                  </div>
                </div>
                <div className="p-2">
                  <p className="text-2xs text-ink-soft line-clamp-2">
                    {draggedBox.prompt || 'No prompt'}
                  </p>
                </div>
              </div>
            </div>,
            document.body
          );
        })()}
      </div>

      {/* FOOTER */}
      <div className="shrink-0 bg-white px-3 py-2.5 flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-t border-cream-200">
        <div className="flex items-center gap-2">
          <button
            onClick={removeRow}
            disabled={config.rows <= 1 || isAnyGenerating}
            className="ns-icon-btn ns-icon-btn-sm ns-icon-btn-line"
            title="Remove row"
            aria-label="Remove row"
          >
            <Minus />
          </button>
          <span className="ns-t-small whitespace-nowrap">{config.rows} {config.rows === 1 ? 'row' : 'rows'}</span>
          <button
            onClick={addRow}
            disabled={isAnyGenerating}
            className="ns-icon-btn ns-icon-btn-sm ns-icon-btn-line"
            title="Add row"
            aria-label="Add row"
          >
            <Plus />
          </button>
        </div>

        <div className="flex items-center gap-3 ml-auto">
          <span className="ns-t-meta whitespace-nowrap">
            {boxesWithPrompts} {boxesWithPrompts === 1 ? 'prompt' : 'prompts'} ready
          </span>

          {isAnyGenerating ? (
            <button
              onClick={stopGeneration}
              className="ns-btn ns-btn-primary ns-btn-xs"
            >
              <Square size={12} fill="currentColor" />
              Stop
            </button>
          ) : (
            <button
              onClick={generateAll}
              disabled={boxesWithPrompts === 0}
              className="ns-btn ns-btn-primary ns-btn-xs"
            >
              <Play size={12} fill="currentColor" />
              Generate all
            </button>
          )}
        </div>
      </div>

      {/* API Key Prompt */}
      {showApiKeyPrompt && (
        <div className="absolute inset-0 z-20 bg-white/95 flex flex-col items-center justify-center p-4 text-center">
          <span className="ns-mark ns-mark-sm mb-2" data-tone="warn"><AlertCircle /></span>
          <p className="ns-t-title mb-2">API key required</p>
          <button
            onClick={handleSelectKey}
            className="ns-btn ns-btn-primary ns-btn-xs"
          >
            Set key
          </button>
        </div>
      )}
    </div>
  );
};

// Entity mention type for @-mentions
interface EntityMention {
  id: string;
  name: string;
  type: string;
  /** The colour of the entity's kind of node: its tag in a prompt and its row in the menu carry it. */
  tone?: NodeTone;
}

// Individual Storyboard Box Component
interface StoryboardBoxComponentProps {
  box: StoryboardBox;
  index: number;
  aspectRatio: string;
  textareaHeight: number;
  onTextareaResize: (height: number) => void;
  isGenerating: boolean;
  disabled: boolean;
  onPromptChange: (prompt: string) => void;
  onGenerate: () => void;
  onDismissError: () => void;
  userId?: string;
  workspaceName?: string;
  isDragging?: boolean;
  isDragOver?: boolean;
  showDropIndicator?: boolean;
  showDropIndicatorAfter?: boolean;
  onPointerDown?: (e: React.PointerEvent) => void;
  registerRef?: (el: HTMLDivElement | null) => void;
  availableEntities?: EntityMention[];
  onDelete?: () => void;
}

const StoryboardBoxComponent: React.FC<StoryboardBoxComponentProps> = ({
  box,
  index,
  aspectRatio,
  textareaHeight,
  onTextareaResize,
  isGenerating,
  disabled,
  onPromptChange,
  onGenerate,
  onDismissError,
  userId,
  workspaceName,
  isDragging = false,
  isDragOver = false,
  showDropIndicator = false,
  showDropIndicatorAfter = false,
  onPointerDown,
  registerRef,
  availableEntities = [],
  onDelete,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // @-mention state
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [mentionStartPos, setMentionStartPos] = useState(0);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const [activeTagId, setActiveTagId] = useState<string | null>(null);
  const [tagDropdownPos, setTagDropdownPos] = useState<{ x: number; y: number } | null>(null);
  const [mentionDropdownPos, setMentionDropdownPos] = useState<{ x: number; y: number } | null>(null);
  const [isTextareaFocused, setIsTextareaFocused] = useState(false);

  // Filter available entities based on mention filter
  const filteredEntities = useMemo(() => {
    if (!mentionFilter) return availableEntities;
    const lower = mentionFilter.toLowerCase();
    return availableEntities.filter(e => e.name.toLowerCase().includes(lower));
  }, [availableEntities, mentionFilter]);

  // Reset selected index when filtered list changes
  useEffect(() => {
    setSelectedMentionIndex(0);
  }, [filteredEntities.length]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowMentionDropdown(false);
        setActiveTagId(null);
        setTagDropdownPos(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle text input for @-mention detection
  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart || 0;
    
    // Find if we're in an @-mention context
    const textBeforeCursor = value.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      // Check if there's a space or newline between @ and cursor (would break the mention)
      if (!/[\s\n]/.test(textAfterAt)) {
        setShowMentionDropdown(true);
        setMentionFilter(textAfterAt);
        setMentionStartPos(lastAtIndex);
        
        // Position dropdown below the current line of text
        if (textareaRef.current) {
          const rect = textareaRef.current.getBoundingClientRect();
          const computed = window.getComputedStyle(textareaRef.current);
          const paddingTop = parseFloat(computed.paddingTop) || 8;
          const lineHeight = parseFloat(computed.lineHeight) || 18;
          
          // Estimate cursor line based on text before cursor
          const lines = textBeforeCursor.split('\n');
          const currentLine = lines.length - 1;
          
          // Position below the current line
          setMentionDropdownPos({
            x: rect.left + 8,
            y: rect.top + paddingTop + ((currentLine + 1) * lineHeight) + 4
          });
        }
      } else {
        setShowMentionDropdown(false);
        setMentionFilter('');
      }
    } else {
      setShowMentionDropdown(false);
      setMentionFilter('');
    }
    
    onPromptChange(value);
  };

  // Handle keyboard navigation in dropdown
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!showMentionDropdown || filteredEntities.length === 0) return;
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedMentionIndex(prev => Math.min(prev + 1, filteredEntities.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedMentionIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      if (showMentionDropdown && filteredEntities[selectedMentionIndex]) {
        e.preventDefault();
        insertMention(filteredEntities[selectedMentionIndex]);
      }
    } else if (e.key === 'Escape') {
      setShowMentionDropdown(false);
    }
  };

  // Insert a mention tag into the text
  const insertMention = (entity: EntityMention) => {
    const currentText = box.prompt;
    const before = currentText.substring(0, mentionStartPos);
    // Calculate end of typed mention: @ symbol (1 char) + filter text
    const typedMentionEnd = mentionStartPos + 1 + mentionFilter.length;
    const after = currentText.substring(typedMentionEnd);
    
    // Insert as @EntityName format (no brackets)
    const mentionTag = `@${entity.name}`;
    const newText = before + mentionTag + (after.startsWith(' ') ? '' : ' ') + after;
    
    onPromptChange(newText);
    setShowMentionDropdown(false);
    setMentionFilter('');
    
    // Focus back on textarea
    setTimeout(() => {
      if (textareaRef.current) {
        const newCursorPos = before.length + mentionTag.length + 1;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  // Replace an existing mention tag
  const replaceMention = (oldName: string, newEntity: EntityMention | null) => {
    const oldTag = `@${oldName}`;
    let newText = box.prompt;
    
    if (newEntity) {
      newText = newText.replace(oldTag, `@${newEntity.name}`);
    } else {
      // Remove the tag entirely (just remove the @)
      newText = newText.replace(oldTag, oldName);
    }
    
    onPromptChange(newText);
    setActiveTagId(null);
    setTagDropdownPos(null);
  };

  // Parse prompt to render with visual tags
  const renderPromptWithTags = () => {
    const prompt = box.prompt;
    
    // If no entities, just return plain text
    if (availableEntities.length === 0) {
      return prompt;
    }
    
    // Build regex to match @EntityName for all known entities
    // Sort by length descending to match longer names first
    const sortedNames = [...availableEntities].sort((a, b) => b.name.length - a.name.length);
    const escapedNames = sortedNames.map(e => e.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const tagRegex = new RegExp(`@(${escapedNames.join('|')})(?=\\s|$|[.,!?;:])`, 'g');
    
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match;

    while ((match = tagRegex.exec(prompt)) !== null) {
      // Add text before the tag
      if (match.index > lastIndex) {
        parts.push(prompt.substring(lastIndex, match.index));
      }
      
      const entityName = match[1];
      const entity = availableEntities.find(e => e.name === entityName);
      
      parts.push(
        <span
          key={match.index}
          data-tone={entity?.tone}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            const rect = e.currentTarget.getBoundingClientRect();
            setActiveTagId(entityName);
            setTagDropdownPos({ x: rect.left, y: rect.bottom + 4 });
          }}
          className={`cursor-pointer transition-colors pointer-events-auto rounded-sm ${
            entity
              ? 'bg-[color:var(--tone-soft)] text-[color:var(--tone-deep)] hover:bg-[color:var(--tone-line)]'
              : 'bg-danger-soft text-danger-deep'
          }`}
          style={{ padding: 0, margin: 0 }}
          title={entity ? `Click to change or remove` : `Entity "${entityName}" not connected`}
        >
          @{entityName}
        </span>
      );
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < prompt.length) {
      parts.push(prompt.substring(lastIndex));
    }
    
    return parts.length > 0 ? parts : prompt;
  };

  // Check if prompt has any @-mention tags (matches @EntityName for known entities)
  const hasTags = useMemo(() => {
    if (availableEntities.length === 0) return false;
    const sortedNames = [...availableEntities].sort((a, b) => b.name.length - a.name.length);
    const escapedNames = sortedNames.map(e => e.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const tagRegex = new RegExp(`@(${escapedNames.join('|')})(?=\\s|$|[.,!?;:])`, 'g');
    return tagRegex.test(box.prompt);
  }, [box.prompt, availableEntities]);

  // Register ref with parent
  React.useEffect(() => {
    if (registerRef) {
      registerRef(boxRef.current);
    }
    return () => {
      if (registerRef) {
        registerRef(null);
      }
    };
  }, [registerRef]);

  // Calculate aspect ratio for image container
  const getAspectRatioStyle = () => {
    const [w, h] = aspectRatio.split(':').map(Number);
    return { paddingBottom: `${(h / w) * 100}%` };
  };

  // Handle resize event
  const handleMouseUp = useCallback(() => {
    if (textareaRef.current) {
      const newHeight = textareaRef.current.offsetHeight;
      onTextareaResize(newHeight);
    }
  }, [onTextareaResize]);

  return (
    <div className="relative">
      {/* Drop indicator - LEFT side */}
      {showDropIndicator && (
        <div className="absolute -left-1.5 top-0 bottom-0 w-1 z-50">
          <div className="ns-bar w-1 h-full bg-[color:var(--tone-deep)]" />
        </div>
      )}

      {/* Drop indicator - RIGHT side (for last card) */}
      {showDropIndicatorAfter && (
        <div className="absolute -right-1.5 top-0 bottom-0 w-1 z-50">
          <div className="ns-bar w-1 h-full bg-[color:var(--tone-deep)]" />
        </div>
      )}

      <div
        ref={boxRef}
        onPointerDown={!isGenerating && !disabled ? onPointerDown : undefined}
        className={`bg-white rounded-lg overflow-hidden border select-none ${
          isDragging
            ? 'opacity-40 scale-95 border-[color:var(--tone)]'
            : 'border-cream-300 hover:border-[color:var(--tone)] hover:shadow-soft'
        } ${!isGenerating && !disabled ? 'cursor-grab active:cursor-grabbing' : ''}`}
      >

      {/* Frame number + Delete button */}
      <div className="h-7 pl-2 pr-0.5 border-b border-cream-200 flex items-center justify-between">
        <span className="ns-badge ns-badge-tone">#{index + 1}</span>
        {onDelete && (
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="ns-icon-btn ns-icon-btn-xs ns-icon-btn-danger"
            title="Delete frame"
            aria-label="Delete frame"
          >
            <X />
          </button>
        )}
      </div>

      {/* Image area */}
      <div className="relative w-full bg-cream-200" style={getAspectRatioStyle()}>
        <div className="absolute inset-0 flex items-center justify-center">
          {box.imageUrl ? (
            <FileImage
              src={box.imageUrl}
              userId={userId}
              workspaceName={workspaceName}
              className="w-full h-full object-cover"
              alt={`Frame ${index + 1}`}
            />
          ) : isGenerating ? (
            <div className="flex flex-col items-center gap-1">
              <Loader2 size={20} className="animate-spin text-[color:var(--tone-deep)]" />
              <span className="ns-t-meta">Generating...</span>
            </div>
          ) : (
            <div className="text-ink-faint">
              <LayoutGrid size={24} />
            </div>
          )}

          {/* Error overlay */}
          {box.status === 'error' && box.errorMessage && (
            <div className="absolute inset-0 bg-danger-soft/95 flex flex-col items-center justify-center gap-1 p-2 text-center">
              <AlertCircle size={16} className="text-danger" />
              <p className="text-2xs text-danger-deep line-clamp-2" title={box.errorMessage}>{box.errorMessage}</p>
              <button
                onClick={onDismissError}
                className="ns-btn ns-btn-ghost ns-btn-xs bg-white px-2.5 py-0.5"
              >
                Dismiss
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Prompt + Generate */}
      <div className="p-2 space-y-2">
        <div className="relative" ref={dropdownRef}>
          {/* Textarea - base layer for editing. While the tags are drawn over it (below), its own text is hidden */}
          <textarea
            ref={textareaRef}
            className={`ns-field block px-2 py-1.5 ${
              hasTags && !isTextareaFocused ? 'text-transparent disabled:text-transparent caret-ink' : ''
            }`}
            placeholder={availableEntities.length > 0 ? "Describe this frame... (type @ to mention characters)" : "Describe this frame..."}
            value={box.prompt}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            onMouseUp={handleMouseUp}
            onFocus={() => setIsTextareaFocused(true)}
            onBlur={(e) => {
              // Small delay to allow tag clicks to register
              setTimeout(() => {
                setIsTextareaFocused(false);
              }, 150);
            }}
            disabled={isGenerating || disabled}
            style={{ height: `${textareaHeight}px`, minHeight: '40px' }}
          />

          {/* Visual tag overlay - only shown when not focused. Its text has to lie exactly on the
              field's: the same size and line height, and the field's padding plus its 1px border
              (and, where a pointer hovers, the 10px a card keeps free for the scrollbar of a field) */}
          {hasTags && !isTextareaFocused && (
            <div
              className="absolute inset-0 px-[9px] py-[7px] [@media(hover:hover)]:pr-[19px] text-[13px] leading-5 text-ink rounded-md overflow-hidden pointer-events-none z-10"
              style={{ height: `${textareaHeight}px`, minHeight: '40px' }}
            >
              <div className="whitespace-pre-wrap break-words">
                {renderPromptWithTags()}
              </div>
            </div>
          )}

          {/* @-mention dropdown - rendered via portal to escape canvas transforms */}
          {showMentionDropdown && availableEntities.length > 0 && mentionDropdownPos && createPortal(
            <div
              role="listbox"
              className="ns-menu fixed z-[130] max-h-48 overflow-y-auto"
              style={{
                left: mentionDropdownPos.x,
                top: mentionDropdownPos.y,
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {filteredEntities.length === 0 ? (
                <div className="ns-t-small px-2.5 py-1.5">No matching characters</div>
              ) : (
                filteredEntities.map((entity, idx) => (
                  <div
                    key={entity.id}
                    role="option"
                    aria-selected={idx === selectedMentionIndex}
                    onClick={() => insertMention(entity)}
                    className="ns-menu-item"
                  >
                    <span
                      data-tone={entity.tone}
                      className="ns-mark [--size:20px] text-2xs font-semibold"
                    >
                      {entity.name.charAt(0).toUpperCase()}
                    </span>
                    <span className="truncate">{entity.name}</span>
                    <span className="ns-t-meta ml-auto pl-2">{entity.type}</span>
                  </div>
                ))
              )}
            </div>,
            document.body
          )}
        </div>

        {/* Tag edit dropdown - rendered via portal to escape canvas transforms */}
        {activeTagId && tagDropdownPos && createPortal(
          <div
            className="ns-menu fixed z-[130] max-h-48 overflow-y-auto"
            style={{ left: tagDropdownPos.x, top: tagDropdownPos.y }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {availableEntities.filter(e => e.name !== activeTagId).map(entity => (
              <div
                key={entity.id}
                onClick={() => replaceMention(activeTagId, entity)}
                className="ns-menu-item"
              >
                <span
                  data-tone={entity.tone}
                  className="ns-mark [--size:20px] text-2xs font-semibold"
                >
                  {entity.name.charAt(0).toUpperCase()}
                </span>
                <span className="truncate">{entity.name}</span>
                <span className="ns-t-meta ml-auto pl-2">{entity.type}</span>
              </div>
            ))}
            <div className="ns-menu-sep" />
            <div
              onClick={() => replaceMention(activeTagId, null)}
              className="ns-menu-item"
              data-danger="true"
            >
              <X />
              <span>Remove tag</span>
            </div>
          </div>,
          document.body
        )}

        <button
          onClick={onGenerate}
          disabled={!box.prompt.trim() || isGenerating || disabled}
          // While its frame is being made the button is at rest, but not dimmed
          className={`ns-btn ns-btn-tone ns-btn-xs w-full ${isGenerating ? 'disabled:opacity-100' : ''}`}
        >
          {isGenerating ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              Generating
            </>
          ) : (
            <>
              <Sparkles size={14} />
              Generate
            </>
          )}
        </button>
      </div>
      </div>
    </div>
  );
};

export default StoryboardNode;
